import {
  FlexProps,
  Flex,
  Icon,
  Skeleton,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuDivider,
  Text,
  Box,
  Collapse,
  SimpleGrid,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { BsChevronDown, BsGiftFill } from 'react-icons/bs'
import { useTranslation } from 'next-i18next/pages'
import { useBalanceStore } from '@/context/UserBalance'
import { Amount } from '../Amount/Amount'
import { getTokens } from '@/services/token-service'
import { useShallow } from 'zustand/react/shallow'
import { LocalStorageKey } from '@/constants/localstorage'
import InfoIcon from '@/public/assets/icons/info.svg'
import ArrowUpRightIcon from '@/public/assets/icons/arrow-up-right.svg'
import GamepadIcon from '@/public/assets/icons/game-controller-solid.svg'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { GameRules } from 'types/types'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { USDC_NEW_DENOM, IS_FIAT_ENABLED, SHU_DENOM } from '@/constants/constants'
import { useCashierFullBalance } from '@/context/CashierProvider'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { useRouter } from 'next/router'
import { GAME_ZONE_TOKEN_CHANGE_REQUEST_EVENT } from '@/components/molecules/GameZoneSessionModal/GameZoneSessionModal'
import { useGameZonePreferences } from '@/context/GameZonePreferences'

const bonusGradient = `linear-gradient(90deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.25) 25%, rgba(255, 255, 255, 0.5) 33%, rgba(255, 255, 255, 0.25) 66%, rgba(255, 255, 255, 0.25) 75%, rgba(255, 255, 255, 0.25) 100%)`
const menuBg = '#1D1C49'
const menuSectionBg = '#15143D'
const menuMuted = '#6966A4'
const menuBorder = '#282759'
const menuAccent = '#68F276'
const menuMutedStrong = '#8784B7'

export const UserBalance = ({ wrapperProps }: { wrapperProps?: FlexProps }) => {
  const { t } = useTranslation()
  const [fiatExpanded, setFiatExpanded] = useState(false)
  const { fullBalance, viewBalance, isLoading, denom, isFrozen } = useBalanceStore(
    useShallow((state) => ({
      fullBalance: state.balance,
      viewBalance: state.viewBalance,
      isLoading: state.isLoading,
      denom: state.denom,
      isFrozen: state.freezedBalanceStatus,
    }))
  )
  const modal = useAppModal()
  const fiat = useFiatCurrencyStore()
  const { gameBalance, hasGameBalance, hasGameBalancePrice } = useCashierFullBalance()
  const isFiatActive = useMemo(() => !fiat.isUsd() && denom === USDC_NEW_DENOM, [fiat.selectedCurrency, denom])

  const router = useRouter()
  const isGameZone = useMemo(() => router.pathname.startsWith('/game-zone/'), [router.pathname])
  const isGameZoneCurrencyLocked = useGameZonePreferences((state) => state.isCurrencySelectionLocked)
  const isCurrencySelectionDisabled = isFrozen || (isGameZone && isGameZoneCurrencyLocked)

  if (isLoading) {
    return <Skeleton startColor={'ninja.600'} endColor={'ninja.500'} width={'135px'} h={12} rounded={'2xl'} />
  }

  const handleSelectCrypto = (newDenom: string) => {
    if (newDenom === denom) return

    if (isGameZone) {
      window.dispatchEvent(
        new CustomEvent(GAME_ZONE_TOKEN_CHANGE_REQUEST_EVENT, {
          detail: { denom: newDenom },
        })
      )
      return
    }

    useBalanceStore.setState({ denom: newDenom })
    localStorage.setItem(LocalStorageKey.Denom, JSON.stringify(newDenom))
    fiat.setCurrency('USD')
  }

  const handleSelectFiat = (code: string) => {
    fiat.setCurrency(code)
    useBalanceStore.setState({ denom: USDC_NEW_DENOM })
    localStorage.setItem(LocalStorageKey.Denom, JSON.stringify(USDC_NEW_DENOM))
  }

  const balance = viewBalance[denom]?.humanLocaleString || '0.00'
  const tokens = getTokens().filter((token) => !isGameZone || token.denom !== SHU_DENOM)
  const activeToken = tokens.find((token) => token.denom === denom) || tokens[0]
  const gameZoneFunds = gameBalance.toNumber() || 0
  const formattedGameZoneFunds = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(gameZoneFunds)
  const bonusTokenData = [activeToken, ...tokens.filter((token) => token.denom !== activeToken?.denom)]
    .map((token) => {
      const tokenBalance = fullBalance[token.denom]
      const totalBonus = new TokenMoney(
        tokenBalance.bonus.chainValue.plus(tokenBalance.game_bonus_balance.chainValue),
        token.denom
      )

      return {
        token,
        totalBonus,
      }
    })
    .find(({ totalBonus }) => totalBonus.chainValue.gt(0))
  const hasBonusFunds = Boolean(bonusTokenData)

  const fiatCurrencies = Object.keys(fiat.rates).filter((c) => c !== 'USD')

  // USDC balance and bonus as numbers for fiat conversion
  const usdcBank = Number(fullBalance[USDC_NEW_DENOM]?.bank?.humanFormat || '0')
  const usdcBonus = Number(fullBalance[USDC_NEW_DENOM]?.bonus?.humanFormat || '0')

  const fiatAmount = (usdc: number, code: string) => {
    const v = usdc * (fiat.rates[code] ?? 1)
    return v >= 1000 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)
  }

  return (
    <>
      <Flex
        h={12}
        border={'1px solid'}
        borderColor={'ninja.500'}
        rounded={'2xl'}
        overflow={'hidden'}
        alignItems={'center'}
        opacity={isCurrencySelectionDisabled ? 0.6 : 1}
        bg={'ninja.600'}
      >
        <Menu placement="bottom-end" closeOnSelect={true}>
          <MenuButton
            _hover={{ bg: isCurrencySelectionDisabled ? 'transparent' : 'ninja.500' }}
            disabled={isCurrencySelectionDisabled}
            color={'ninja.200'}
            transition={'0.2s'}
            cursor={isCurrencySelectionDisabled ? 'not-allowed' : 'pointer'}
            h={'full'}
            bg={'transparent'}
            px={0}
            minW={'132px'}
          >
            <Flex alignItems={'center'} gap={2} pl={4} pr={3} fontWeight={700} fontSize={'sm'} {...wrapperProps}>
              <Flex alignItems={'center'} gap={1.5}>
                {isFiatActive ? (
                  <>
                    <Text fontSize={'sm'} color={'white'} fontWeight={700} whiteSpace={'nowrap'}>
                      {fiat.formatBalance(Number(viewBalance[denom]?.humanFormat || '0'))}
                    </Text>
                    <Text fontSize={'xs'} color={'ninja.300'} fontWeight={500} whiteSpace={'nowrap'}>
                      ≈{balance} USDC
                    </Text>
                  </>
                ) : (
                  <Amount iconSize={'18px'} value={balance} denom={denom} amountStyles={{ color: 'white' }} />
                )}
              </Flex>
              <Icon color={'ninja.100'} as={BsChevronDown} />
            </Flex>
          </MenuButton>

          <MenuList
            p={0}
            bg={menuBg}
            border={'none'}
            w={{ base: '268px', sm: '308px' }}
            minW={'unset'}
            maxW={'calc(100vw - 24px)'}
            rounded={'20px'}
            boxShadow={'0px 8px 48px 0px rgba(0, 0, 0, 0.20)'}
            zIndex={60}
            maxH={'520px'}
            overflowY={'auto'}
            overflowX={'hidden'}
          >
            <Box px={3.5} pt={4} pb={3}>
              <Text fontSize={'12px'} fontWeight={700} color={menuMuted}>
                {t('common.crypto')} {t('common.balance')}
              </Text>
            </Box>

            <Box px={1} pb={1.5}>
              {tokens.map((token, index) => {
                const isActive = denom === token.denom && fiat.isUsd()
                const isShuToken = token.denom === SHU_DENOM
                const hasGameBalance = fullBalance[token.denom].game_balance.chainValue.gt(0)
                const walletAndGameBalance = new TokenMoney(
                  fullBalance[token.denom].bank.chainValue.plus(
                    !isActive ? fullBalance[token.denom].game_main_balance.chainValue : 0
                  ),
                  token.denom
                )
                const rowBonusBalance = new TokenMoney(
                  fullBalance[token.denom].bonus.chainValue.plus(
                    !isActive ? fullBalance[token.denom].game_bonus_balance.chainValue : 0
                  ),
                  token.denom
                )
                const showGiftAction = isShuToken && fullBalance[token.denom].amount.humanNumber <= 100

                return (
                  <MenuItem
                    key={index}
                    closeOnSelect
                    bg={isActive ? menuSectionBg : 'transparent'}
                    _hover={{ bg: isActive ? menuSectionBg : 'rgba(21, 20, 61, 0.65)' }}
                    px={2}
                    py={2.5}
                    rounded={'12px'}
                    w={'full'}
                    display={'block'}
                    textAlign={'left'}
                    onClick={() => handleSelectCrypto(token.denom)}
                  >
                    <Flex alignItems={'flex-start'} gap={3} justifyContent={'space-between'}>
                      <Flex alignItems={'flex-start'} gap={3} minW={0}>
                        <Box h={'20px'} w={'20px'} flexShrink={0} mt={0.5}>
                          {token.icon('20px')}
                        </Box>
                        <Flex direction={'column'} gap={isActive && hasGameBalance ? 1 : 0} minW={0}>
                          <Flex gap={1} alignItems={'baseline'} flexWrap={'wrap'}>
                            <Text fontSize={'16px'} fontWeight={700} color={'white'} lineHeight={1.1}>
                              {walletAndGameBalance.humanFormat}
                            </Text>
                            {!rowBonusBalance.chainValue.isEqualTo(0) && (
                              <Text
                                bg={'#8B60FA'}
                                bgBlendMode={'overlay'}
                                bgGradient={bonusGradient}
                                backgroundClip={'text'}
                                __css={{ '-webkit-text-fill-color': 'transparent' }}
                                fontSize={'10px'}
                                fontWeight={700}
                                lineHeight={1}
                              >
                                {rowBonusBalance.humanFormat}
                              </Text>
                            )}
                            <Text fontSize={'16px'} fontWeight={700} color={menuMuted} lineHeight={1.1}>
                              {token.name}
                            </Text>
                          </Flex>

                          {isActive && hasGameBalance && (
                            <Text fontSize={'12px'} fontWeight={700} color={menuMuted} lineHeight={1.1}>
                              {t('common.in-game')}{' '}
                              <Text as={'span'} color={'#DCD9FF'}>
                                {fullBalance[token.denom].game_balance.humanLocaleString}
                              </Text>{' '}
                              {token.name}
                            </Text>
                          )}
                        </Flex>
                      </Flex>

                      <Flex alignItems={'center'} gap={2} flexShrink={0}>
                        {hasGameBalance && (
                          <Flex
                            alignItems={'center'}
                            gap={1}
                            px={2}
                            py={1}
                            rounded={'6px'}
                            border={'1px solid'}
                            borderColor={menuAccent}
                            bg={menuBg}
                          >
                            <Icon as={GamepadIcon} w={'12px'} h={'12px'} color={menuAccent} />
                            <Text fontSize={'11px'} fontWeight={600} color={menuAccent} lineHeight={1}>
                              {t('common.in-game')}
                            </Text>
                          </Flex>
                        )}

                        {showGiftAction && (
                          <Box
                            as="button"
                            type="button"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation()
                              modal.openModalNew(AppModals.ShuFaucet)
                            }}
                            w={'28px'}
                            h={'28px'}
                            borderRadius={'8px'}
                            bg={menuBorder}
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            transition={'0.2s'}
                            _hover={{ bg: '#37356F' }}
                            title={t('shu-faucet.get-shu')}
                          >
                            <Icon as={BsGiftFill} boxSize={'12px'} color={'#DCD9FF'} flexShrink={0} />
                          </Box>
                        )}
                      </Flex>
                    </Flex>
                  </MenuItem>
                )
              })}
            </Box>

            {hasGameBalance && (
              <Box borderTop={'1px solid'} borderColor={menuBorder} px={3.5} py={3}>
                <Text fontSize={'12px'} fontWeight={700} color={menuMuted} mb={3}>
                  {t('game-zone.title')}
                </Text>

                <Flex alignItems={'center'} justifyContent={'space-between'} gap={3}>
                  <Flex alignItems={'center'} gap={2.5} minW={0}>
                    <Flex
                      boxSize={'20px'}
                      rounded={'10px'}
                      bg={menuBg}
                      alignItems={'center'}
                      justifyContent={'center'}
                      flexShrink={0}
                    >
                      <Icon as={GamepadIcon} w={'12px'} h={'12px'} color={'#D7CAFF'} />
                    </Flex>
                    <Flex direction={'column'} gap={0.5} minW={0}>
                      <Text fontSize={'16px'} fontWeight={700} color={'white'} lineHeight={1.1} whiteSpace={'nowrap'}>
                        {hasGameBalancePrice ? `≈ $${formattedGameZoneFunds}` : '—'}
                      </Text>
                    </Flex>
                  </Flex>

                  <Button
                    variant={'ghost'}
                    px={0}
                    h={'44px'}
                    minW={'unset'}
                    color={'transparent'}
                    _hover={{ bg: 'transparent', opacity: 0.9 }}
                    _active={{ bg: 'transparent' }}
                    onClick={() => modal.openModalNew(AppModals.GameZoneWithdraw)}
                    isDisabled={isFrozen}
                  >
                    <Flex alignItems={'center'} gap={1.5}>
                      <Icon as={ArrowUpRightIcon} w={'14px'} h={'14px'} color={'#C5B7FF'} />
                      <Text
                        fontSize={'14px'}
                        fontWeight={700}
                        bgGradient={'linear(159deg, #DAC7FF 3.53%, #8B60FA 107.54%)'}
                        backgroundClip={'text'}
                        __css={{ '-webkit-text-fill-color': 'transparent' }}
                      >
                        {t('common.withdraw')}
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </Box>
            )}

            {/* Fiat section — single row, expands on click */}
            {IS_FIAT_ENABLED && (
              <>
                <MenuDivider borderColor={menuBorder} mx={3.5} my={0} />
                <MenuItem
                  closeOnSelect={false}
                  bg={isFiatActive ? menuSectionBg : 'transparent'}
                  _hover={{ bg: menuSectionBg }}
                  px={3.5}
                  py={2.5}
                  rounded={'none'}
                  fontSize={'sm'}
                  onClick={() => setFiatExpanded((v) => !v)}
                >
                  <Flex alignItems={'center'} gap={1.5} w={'full'} justifyContent={'space-between'}>
                    <Flex alignItems={'center'} gap={1.5}>
                      <Flex h={'18px'} w={'18px'} alignItems={'center'} justifyContent={'center'} flexShrink={0}>
                        <Text fontSize={'14px'} fontWeight={700} color={'#DCD9FF'} lineHeight={1}>
                          {isFiatActive ? fiat.symbols[fiat.selectedCurrency] : '$'}
                        </Text>
                      </Flex>
                      <Flex gap={0.5} whiteSpace={'nowrap'} fontWeight={700} alignItems={'center'}>
                        {isFiatActive ? (
                          <>
                            <Text as="span">{fiatAmount(usdcBank, fiat.selectedCurrency)}</Text>
                            {usdcBonus > 0 && (
                              <Text
                                bg={'#8B60FA'}
                                bgBlendMode={'overlay'}
                                bgGradient={bonusGradient}
                                backgroundClip={'text'}
                                __css={{ '-webkit-text-fill-color': 'transparent' }}
                                pb={1}
                                fontSize={'10px'}
                                as="span"
                              >
                                {fiatAmount(usdcBonus, fiat.selectedCurrency)}
                              </Text>
                            )}
                            <Text as="span" color={'ninja.300'}>
                              {fiat.selectedCurrency}
                            </Text>
                          </>
                        ) : (
                          <Text as="span" color={menuMutedStrong} fontWeight={500}>
                            {t('common.fiat-display')}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                    <Flex alignItems={'center'} gap={1.5}>
                      {isFiatActive && <Box w={'4px'} h={'4px'} borderRadius={'full'} bg={'ninja.100'} />}
                      <Icon
                        as={BsChevronDown}
                        color={menuMutedStrong}
                        w={'10px'}
                        h={'10px'}
                        transform={fiatExpanded ? 'rotate(180deg)' : 'rotate(0)'}
                        transition={'0.2s'}
                      />
                    </Flex>
                  </Flex>
                </MenuItem>
                <Collapse in={fiatExpanded} animateOpacity>
                  <Box px={3.5} pt={1} pb={3}>
                    <SimpleGrid columns={3} spacing={'4px'}>
                      <Box
                        as="button"
                        onClick={() => {
                          fiat.setCurrency('USD')
                          setFiatExpanded(false)
                        }}
                        bg={fiat.isUsd() ? 'ninja.400' : 'ninja.600'}
                        rounded={'lg'}
                        px={2}
                        py={1.5}
                        fontSize={'11px'}
                        fontWeight={600}
                        color={fiat.isUsd() ? 'white' : 'ninja.300'}
                        textAlign={'center'}
                        cursor={'pointer'}
                        _hover={{ bg: 'ninja.400' }}
                        transition={'0.15s'}
                      >
                        {t('common.off')}
                      </Box>
                      {fiatCurrencies.map((code) => {
                        const isActive = isFiatActive && fiat.selectedCurrency === code
                        return (
                          <Box
                            as="button"
                            key={code}
                            onClick={() => {
                              handleSelectFiat(code)
                              setFiatExpanded(false)
                            }}
                            bg={isActive ? 'ninja.400' : 'ninja.600'}
                            rounded={'lg'}
                            px={2}
                            py={1.5}
                            fontSize={'11px'}
                            fontWeight={600}
                            color={isActive ? 'white' : 'ninja.300'}
                            textAlign={'center'}
                            cursor={'pointer'}
                            _hover={{ bg: 'ninja.400' }}
                            transition={'0.15s'}
                          >
                            {fiat.symbols[code]} {code}
                          </Box>
                        )
                      })}
                    </SimpleGrid>
                  </Box>
                </Collapse>

                {/* Footer info */}
                {!fiat.isUsd() && (
                  <Flex
                    alignItems={'center'}
                    gap={1}
                    borderTop={'1px solid'}
                    borderColor={menuBorder}
                    pt={3}
                    px={3.5}
                    pb={3}
                  >
                    <Icon as={InfoIcon} color={menuMutedStrong} w={'12px'} h={'12px'} />
                    <Text fontSize={'10px'} color={menuMutedStrong} fontWeight={500}>
                      {t('common.balance-in-usdc')}
                    </Text>
                  </Flex>
                )}
              </>
            )}
            {hasBonusFunds && (
              <Flex
                cursor={'pointer'}
                alignItems={'center'}
                justifyContent={'space-between'}
                gap={3}
                borderTop={'1px solid'}
                borderColor={menuBorder}
                px={3.5}
                py={3}
                onClick={() => modal.openModalNew(AppModals.GameRules, { game: GameRules.BONUS_FUNDS })}
              >
                <Flex alignItems={'center'} gap={1.5}>
                  <Text
                    fontWeight={700}
                    bgGradient={'linear(163deg, #DAC7FF 3.53%, #8B60FA 107.54%)'}
                    backgroundClip={'text'}
                    __css={{ '-webkit-text-fill-color': 'transparent' }}
                    fontSize={'14px'}
                    userSelect={'none'}
                  >
                    {t('common.bonus-funds')}
                  </Text>
                  <Icon as={InfoIcon} color={menuMutedStrong} w={'14px'} h={'14px'} />
                </Flex>
              </Flex>
            )}
          </MenuList>
        </Menu>
      </Flex>
    </>
  )
}
